import React, { useEffect } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { servicesData } from '../utils/mockData';

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 100; // 100px offset from top
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <section className="bg-gradient-to-r from-cyan-600 to-emerald-600 py-10">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Our Services
          </h1>
          <p className="text-xl text-cyan-50 max-w-2xl mx-auto">
            Professional cleaning solutions tailored to your needs. Quality service you can trust.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="space-y-16 max-w-6xl mx-auto">
            {servicesData.map((service, index) => (
              <div
                key={service.id}
                id={`service-${service.id}`}
                className={`grid lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  {service.beforeAfterImages ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                        <img
                          src={service.beforeAfterImages[0]}
                          alt={`${service.title} - Before`}
                          className="w-full h-[480px] object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                          <span className="text-sm font-bold text-gray-900">Before</span>
                        </div>
                      </div>
                      <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                        <img
                          src={service.beforeAfterImages[1]}
                          alt={`${service.title} - After`}
                          className="w-full h-[480px] object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                          <span className="text-sm font-bold text-gray-900">After</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-[480px] object-cover group-hover:scale-110 transition-transform duration-500"
                        style={
                          service.title === 'Gutter Cleaning' ? { objectPosition: 'center 20%' } :
                          service.title === 'Window Cleaning' ? { objectPosition: 'center 10%' } : {}
                        }
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    </div>
                  )}
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center mt-0.5">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white group">
                      Book This Service
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Why Choose Our Services?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We combine professional expertise with a commitment to customer satisfaction.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="border-2 border-gray-100 hover:border-cyan-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-3">🛡️</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Fully Insured</h3>
                  <p className="text-sm text-gray-600">
                    Complete protection and peace of mind
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-3">⚡</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Professional Equipment</h3>
                  <p className="text-sm text-gray-600">
                    Latest tools for the best results
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-100 hover:border-cyan-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-3">⭐</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Experienced Team</h3>
                  <p className="text-sm text-gray-600">
                    Years of expertise you can trust
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-3">♻️</div>
                  <h3 className="font-semibold text-gray-900 mb-2">Eco-Friendly</h3>
                  <p className="text-sm text-gray-600">
                    Environmentally conscious cleaning
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Guarantee */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Our Service Guarantee
            </h2>
            <Card className="border-2 border-cyan-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  We take pride in delivering exceptional results every time. If you're not completely satisfied with our work, we'll return and make it right - free of charge. That's our commitment to you.
                </p>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-cyan-600 mb-2">99.9%</div>
                    <p className="text-sm text-gray-600">Customer Satisfaction</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-emerald-600 mb-2">3000+</div>
                    <p className="text-sm text-gray-600">Happy Customers Over 20 Years</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-cyan-600 mb-2">Approx 20 mile</div>
                    <p className="text-sm text-gray-600">Service Radius</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-cyan-600 to-emerald-600 rounded-2xl p-12 text-white">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-cyan-50 mb-8">
              Get in touch today for a free quote and let us show you what professional service looks like.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100">
                  Get a Free Quote
                </Button>
              </Link>
              <Link to="/estimates">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
